// Slack-specific extras for a Turn: reactions as small inline chips,
// thread-reply indicator, optional permalink to the message in Slack.

import type { Turn, SlackTurnMeta } from "./types";

interface Props {
  turn: Turn;
}

export function SlackTurnExtras({ turn }: Props): React.ReactElement | null {
  const meta = (turn.metadata ?? {}) as SlackTurnMeta;
  const reactions = meta.reactions ?? [];
  const isThreadReply = Boolean(meta.thread_ts && meta.thread_ts !== meta.message_ts);
  const permalink = meta.permalink;

  if (reactions.length === 0 && !isThreadReply && !permalink) {
    return null;
  }

  return (
    <div className="cv-slack-extras">
      {isThreadReply && (
        <span className="cv-thread-tag" title="Reply in a thread">↳ thread</span>
      )}
      {reactions.map((r) => (
        <span key={r.name} className="cv-reaction" title={`:${r.name}: × ${r.count}`}>
          {emojify(r.name)}
          <span className="cv-reaction-count">{r.count}</span>
        </span>
      ))}
      {permalink && (
        <a
          className="cv-permalink"
          href={permalink}
          target="_blank"
          rel="noreferrer"
          title="Open in Slack"
        >
          ↗
        </a>
      )}
    </div>
  );
}

// Minimal :emoji_name: → Unicode mapper. Covers the most common
// reactions; falls back to the colon-wrapped name. A full mapping
// belongs in a shared util, not here.
const COMMON_EMOJI: Record<string, string> = {
  "+1": "👍",
  thumbsup: "👍",
  "-1": "👎",
  thumbsdown: "👎",
  heart: "❤️",
  fire: "🔥",
  eyes: "👀",
  white_check_mark: "✅",
  heavy_check_mark: "✔️",
  x: "❌",
  question: "❓",
  rocket: "🚀",
  sparkles: "✨",
  pray: "🙏",
  tada: "🎉",
  warning: "⚠️",
};

function emojify(name: string): string {
  return COMMON_EMOJI[name] ?? `:${name}:`;
}
